/**
 * GraveEffects.tsx
 * Efekty dla konkretnych grobów z nieaktywnym właścicielem:
 *  - FallingLeaves   — liście opadające i leżące na nagrobku (1 liść = 1 tydzień)
 *  - InactivityCounter — licznik dni bez logowania widoczny pod nagrobkiem
 *  - InactiveGraveWrapper — opakowanie grobu aktywujące oba efekty
 */

import { useState, useEffect, useRef } from "react";
import { Leaf, Clock } from "lucide-react";

/* ── data ostatniego logowania (73 dni temu) ── */
export const LAST_LOGIN_DATE = new Date(Date.now() - 73 * 24 * 60 * 60 * 1000);

export function daysSince(date: Date): number {
  return Math.floor((Date.now() - date.getTime()) / (1000 * 60 * 60 * 24));
}

/* ── typy ── */
interface LeafParticle {
  id: number;
  x: number;
  y: number;
  size: number;
  rotation: number;
  rotSpeed: number;
  driftX: number;
  speed: number;
  opacity: number;
  color: string;
  shape: number;
}

const LEAF_COLORS = [
  "#c0622a", "#a8472a", "#d4882e", "#8b3a1a",
  "#b85c2a", "#7a3520", "#e09035", "#954030",
];

/* ── Kształt liścia SVG ── */
function LeafSVG({ shape, color, size }: { shape: number; color: string; size: number }) {
  const paths = [
    "M12 2 C8 6 2 8 2 14 C2 18 6 22 12 22 C18 22 22 18 22 14 C22 8 16 6 12 2 Z M12 4 L12 20",
    "M12 3 C6 3 2 8 2 14 C2 20 6 22 12 22 C18 22 22 20 22 14 C22 8 18 3 12 3 Z M12 5 L12 21",
    "M12 2 C9 5 3 7 4 13 C5 17 8 20 12 21 C16 20 19 17 20 13 C21 7 15 5 12 2 Z M12 4 L12 19",
    "M12 21 C12 21 3 15 3 9 C3 6 5 4 8 4 C10 4 12 6 12 6 C12 6 14 4 16 4 C19 4 21 6 21 9 C21 15 12 21 12 21 Z",
  ];
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill={color}
      xmlns="http://www.w3.org/2000/svg"
      style={{ filter: "drop-shadow(0 1px 2px rgba(0,0,0,0.5))" }}
    >
      <path d={paths[shape % 4]} />
    </svg>
  );
}

/* ──────────────────────────────────────────────────────────
   FallingLeaves
   Renderuje liście wewnątrz wrappera nagrobka.
   weekCount liści leży nieruchomo, co 3s nowy liść opada.
────────────────────────────────────────────────────────── */
export function FallingLeaves({ weekCount }: { weekCount: number }) {
  const [leaves, setLeaves] = useState<LeafParticle[]>([]);
  const animRef = useRef<number | null>(null);
  const leavesRef = useRef<LeafParticle[]>([]);
  const lastSpawnRef = useRef<number>(0);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;

    /* liście leżące — po 1 na każdy tydzień nieaktywności (max 12) */
    const count = Math.min(weekCount, 12);
    const resting: LeafParticle[] = Array.from({ length: count }, (_, i) => ({
      id: i,
      x: 8 + Math.random() * 84,
      y: 55 + Math.random() * 38,
      size: 8 + Math.random() * 7,
      rotation: Math.random() * 360,
      rotSpeed: 0,
      driftX: 0,
      speed: 0,
      opacity: 0.5 + Math.random() * 0.3,
      color: LEAF_COLORS[Math.floor(Math.random() * LEAF_COLORS.length)],
      shape: Math.floor(Math.random() * 4),
    }));
    leavesRef.current = resting;
    setLeaves([...resting]);

    /* animacja nowo opadających liści */
    const animate = (ts: number) => {
      if (!mountedRef.current) return;

      const moving = leavesRef.current.filter(l => l.speed > 0).length;
      if (ts - lastSpawnRef.current > 2800 && moving < 2) {
        lastSpawnRef.current = ts;
        const fresh: LeafParticle = {
          id: Date.now() + Math.random(),
          x: 5 + Math.random() * 90,
          y: -8,
          size: 9 + Math.random() * 8,
          rotation: Math.random() * 360,
          rotSpeed: (Math.random() - 0.5) * 3,
          driftX: (Math.random() - 0.5) * 0.4,
          speed: 0.15 + Math.random() * 0.18,
          opacity: 0.75 + Math.random() * 0.2,
          color: LEAF_COLORS[Math.floor(Math.random() * LEAF_COLORS.length)],
          shape: Math.floor(Math.random() * 4),
        };
        leavesRef.current = [...leavesRef.current, fresh];
      }

      leavesRef.current = leavesRef.current.map(leaf => {
        if (leaf.speed === 0) return leaf;
        const ny = leaf.y + leaf.speed;
        const nx = leaf.x + leaf.driftX + Math.sin(ny * 0.06) * 0.25;
        const nr = leaf.rotation + leaf.rotSpeed;
        /* liść dosięga dołu → zatrzymuje się */
        if (ny > 100) {
          return { ...leaf, y: 60 + Math.random() * 30, x: nx, speed: 0, rotSpeed: 0 };
        }
        return { ...leaf, y: ny, x: nx, rotation: nr };
      });

      setLeaves([...leavesRef.current]);
      animRef.current = requestAnimationFrame(animate);
    };

    animRef.current = requestAnimationFrame(animate);
    return () => {
      mountedRef.current = false;
      if (animRef.current) cancelAnimationFrame(animRef.current);
    };
  }, [weekCount]);

  return (
    <div
      className="absolute inset-0 pointer-events-none overflow-hidden"
      style={{ zIndex: 20, borderRadius: 6 }}
    >
      {leaves.map(leaf => (
        <div
          key={leaf.id}
          className="absolute"
          style={{
            left: `${leaf.x}%`,
            top: `${leaf.y}%`,
            transform: `rotate(${leaf.rotation}deg)`,
            opacity: leaf.opacity,
            transition: "none",
          }}
        >
          <LeafSVG shape={leaf.shape} color={leaf.color} size={leaf.size} />
        </div>
      ))}
    </div>
  );
}

/* ──────────────────────────────────────────────────────────
   InactivityCounter
   Licznik dni/tygodni bez logowania — pojawia się pod
   nagrobkiem po najechaniu myszką.
────────────────────────────────────────────────────────── */
export function InactivityCounter({ lastLogin }: { lastLogin: Date }) {
  const days = daysSince(lastLogin);
  const weeks = Math.floor(days / 7);

  const isAlert = days > 60;
  const isWarn  = days > 30 && days <= 60;

  const borderColor = isAlert
    ? "rgba(220,80,60,0.7)"
    : isWarn
    ? "rgba(212,175,55,0.7)"
    : "rgba(100,180,140,0.6)";

  const dayColor = isAlert
    ? "rgba(240,100,80,0.95)"
    : isWarn
    ? "rgba(220,180,60,0.95)"
    : "rgba(120,200,155,0.95)";

  const leafWord = weeks === 1 ? "liść" : weeks < 5 ? "liście" : "liści";

  return (
    <div
      className="absolute left-1/2 -translate-x-1/2 flex flex-col items-center gap-1 px-3 py-2 rounded-xl pointer-events-none"
      style={{
        bottom: "-72px",
        minWidth: 130,
        background: "oklch(0.08 0.02 230 / 0.92)",
        border: `1px solid ${borderColor}`,
        backdropFilter: "blur(8px)",
        boxShadow: "0 4px 20px rgba(0,0,0,0.5)",
        zIndex: 50,
        whiteSpace: "nowrap",
      }}
    >
      {/* dni bez logowania */}
      <div className="flex items-center gap-1.5">
        <Clock className="w-3 h-3 shrink-0" style={{ color: dayColor }} />
        <span className="text-[11px] font-semibold tabular-nums" style={{ color: dayColor }}>
          {days} dni bez wizyty
        </span>
      </div>

      {/* tygodnie = liście */}
      {weeks > 0 && (
        <div className="flex items-center gap-1.5">
          <Leaf className="w-3 h-3 shrink-0" style={{ color: "#c0622a" }} />
          <span className="text-[10px]" style={{ color: "rgba(180,160,130,0.85)" }}>
            {weeks} {leafWord} opadł{weeks > 1 ? "o" : ""}
          </span>
        </div>
      )}

      {/* ostrzeżenie */}
      {isAlert && (
        <span
          className="text-[9px] font-medium mt-0.5 px-1.5 py-0.5 rounded"
          style={{ background: "rgba(220,80,60,0.15)", color: "rgba(240,100,80,0.9)" }}
        >
          ⚠ Grób zarasta
        </span>
      )}
    </div>
  );
}

/* ──────────────────────────────────────────────────────────
   InactiveGraveWrapper
   Opakuj nim <GraveCard> konkretnego grobu.
   Dodaje: liście na nagrobku + licznik po najechaniu.
────────────────────────────────────────────────────────── */
export function InactiveGraveWrapper({
  children,
  lastLogin,
}: {
  children: React.ReactNode;
  lastLogin: Date;
}) {
  const [hovered, setHovered] = useState(false);
  const weeks = Math.floor(daysSince(lastLogin) / 7);

  return (
    <div
      className="relative"
      style={{ isolation: "isolate" }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {/* liście – zawsze widoczne */}
      <FallingLeaves weekCount={weeks} />

      {/* oryginalny GraveCard */}
      {children}

      {/* licznik – pojawia się po najechaniu */}
      {hovered && <InactivityCounter lastLogin={lastLogin} />}
    </div>
  );
}
